import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CallRoutingTab = () => {
  const [queues, setQueues] = useState([
    {
      id: 1,
      name: 'Satış Kuyruğu',
      extension: '8001',
      strategy: 'ring-all',
      timeout: 30,
      maxWaitTime: 300,
      agents: ['ahmet.yilmaz', 'fatma.demir'],
      status: 'active',
      callsWaiting: 3,
      avgWaitTime: 45
    },
    {
      id: 2,
      name: 'Teknik Destek',
      extension: '8002',
      strategy: 'least-recent',
      timeout: 20,
      maxWaitTime: 600,
      agents: ['mehmet.ozkan'],
      status: 'active',
      callsWaiting: 1,
      avgWaitTime: 120
    },
    {
      id: 3,
      name: 'Müşteri Hizmetleri',
      extension: '8003',
      strategy: 'round-robin',
      timeout: 25,
      maxWaitTime: 180,
      agents: ['fatma.demir'],
      status: 'inactive',
      callsWaiting: 0,
      avgWaitTime: 0
    }
  ]);

  const [routingRules, setRoutingRules] = useState([
    {
      id: 1,
      name: 'Çalışma Saatleri Yönlendirmesi',
      condition: 'time-based',
      timeStart: '09:00',
      timeEnd: '18:00',
      destination: 'queue:8001',
      priority: 1,
      enabled: true
    },
    {
      id: 2,
      name: 'Mesai Dışı Yönlendirmesi',
      condition: 'time-based',
      timeStart: '18:01',
      timeEnd: '08:59',
      destination: 'voicemail',
      priority: 2,
      enabled: true
    },
    {
      id: 3,
      name: 'VIP Müşteri Yönlendirmesi',
      condition: 'caller-id',
      callerPattern: '+90532*',
      destination: 'queue:8002',
      priority: 0,
      enabled: true
    }
  ]);

  const [showAddQueueModal, setShowAddQueueModal] = useState(false);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);

  const [newQueue, setNewQueue] = useState({
    name: '',
    extension: '',
    strategy: 'ring-all',
    timeout: 30,
    maxWaitTime: 300,
    musicOnHold: true,
    announcePosition: true,
    recordCalls: false
  });

  const [newRule, setNewRule] = useState({
    name: '',
    condition: 'time-based',
    timeStart: '09:00',
    timeEnd: '18:00',
    callerPattern: '',
    destination: '',
    priority: 1,
    enabled: true
  });

  const strategyOptions = [
    { value: 'ring-all', label: 'Hepsini Çal' },
    { value: 'round-robin', label: 'Sırayla Dağıt' },
    { value: 'least-recent', label: 'En Az Konuşan' },
    { value: 'random', label: 'Rastgele' },
    { value: 'linear', label: 'Doğrusal' }
  ];

  const conditionOptions = [
    { value: 'time-based', label: 'Zaman Bazlı' },
    { value: 'caller-id', label: 'Arayan Numara' },
    { value: 'queue-status', label: 'Kuyruk Durumu' },
    { value: 'agent-availability', label: 'Temsilci Müsaitliği' }
  ];

  const destinationOptions = [
    { value: 'queue:8001', label: 'Satış Kuyruğu' },
    { value: 'queue:8002', label: 'Teknik Destek' },
    { value: 'queue:8003', label: 'Müşteri Hizmetleri' },
    { value: 'voicemail', label: 'Sesli Mesaj' },
    { value: 'external', label: 'Harici Numara' }
  ];

  const handleAddQueue = () => {
    const queue = {
      id: queues?.length + 1,
      ...newQueue,
      agents: [],
      status: 'active',
      callsWaiting: 0,
      avgWaitTime: 0
    };
    setQueues([...queues, queue]);
    setNewQueue({
      name: '',
      extension: '',
      strategy: 'ring-all',
      timeout: 30,
      maxWaitTime: 300,
      musicOnHold: true,
      announcePosition: true,
      recordCalls: false
    });
    setShowAddQueueModal(false);
  };

  const handleAddRule = () => {
    const rule = {
      id: routingRules?.length + 1,
      ...newRule
    };
    setRoutingRules([...routingRules, rule]);
    setNewRule({
      name: '',
      condition: 'time-based',
      timeStart: '09:00',
      timeEnd: '18:00',
      callerPattern: '',
      destination: '',
      priority: 1,
      enabled: true
    });
    setShowAddRuleModal(false);
  };

  const toggleQueueStatus = (queueId) => {
    setQueues(queues?.map(queue => 
      queue?.id === queueId 
        ? { ...queue, status: queue?.status === 'active' ? 'inactive' : 'active' }
        : queue
    ));
  };

  const toggleRuleStatus = (ruleId) => {
    setRoutingRules(routingRules?.map(rule => 
      rule?.id === ruleId 
        ? { ...rule, enabled: !rule?.enabled }
        : rule
    ));
  };

  const getStrategyLabel = (strategy) => {
    const option = strategyOptions?.find(opt => opt?.value === strategy);
    return option ? option?.label : strategy;
  };

  const getConditionLabel = (condition) => {
    const option = conditionOptions?.find(opt => opt?.value === condition);
    return option ? option?.label : condition;
  };

  return (
    <div className="space-y-8">
      {/* Queue Management */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Arama Kuyrukları</h3>
          <Button
            variant="default"
            onClick={() => setShowAddQueueModal(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Yeni Kuyruk
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Kuyruk Adı</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Dahili</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Strateji</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Bekleyen</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Ort. Bekleme</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">Durum</th>
                <th className="text-left py-3 px-4 font-medium text-slate-900">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {queues?.map((queue) => (
                <tr key={queue?.id} className="hover:bg-slate-50">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-slate-900">{queue?.name}</div>
                      <div className="text-sm text-slate-600">{queue?.agents?.length} temsilci</div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-mono text-sm text-slate-900">{queue?.extension}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-slate-900">{getStrategyLabel(queue?.strategy)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-900">{queue?.callsWaiting}</span>
                      {queue?.callsWaiting > 0 && (
                        <Icon name="Phone" size={14} className="text-warning" />
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-slate-600">{queue?.avgWaitTime}s</span>
                  </td>
                  <td className="py-4 px-4">
                    <button
                      onClick={() => toggleQueueStatus(queue?.id)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        queue?.status === 'active' ?'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full mr-1 ${
                        queue?.status === 'active' ? 'bg-success' : 'bg-error'
                      }`} />
                      {queue?.status === 'active' ? 'Aktif' : 'Pasif'}
                    </button>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" iconName="Edit" />
                      <Button variant="ghost" size="sm" iconName="Users" />
                      <Button variant="ghost" size="sm" iconName="Trash2" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Routing Rules */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Yönlendirme Kuralları</h3>
          <Button
            variant="default"
            onClick={() => setShowAddRuleModal(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Yeni Kural
          </Button>
        </div>

        <div className="space-y-4">
          {routingRules?.map((rule) => (
            <div key={rule?.id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <h4 className="font-medium text-slate-900">{rule?.name}</h4>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    Öncelik: {rule?.priority}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleRuleStatus(rule?.id)}
                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      rule?.enabled 
                        ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
                    }`}
                  >
                    {rule?.enabled ? 'Etkin' : 'Pasif'}
                  </button>
                  <Button variant="ghost" size="sm" iconName="Edit" />
                  <Button variant="ghost" size="sm" iconName="Trash2" />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
                <div>
                  <span className="font-medium">Koşul:</span> {getConditionLabel(rule?.condition)}
                </div>
                {rule?.condition === 'time-based' && (
                  <div>
                    <span className="font-medium">Zaman:</span> {rule?.timeStart} - {rule?.timeEnd}
                  </div>
                )}
                {rule?.condition === 'caller-id' && (
                  <div>
                    <span className="font-medium">Numara:</span> {rule?.callerPattern}
                  </div>
                )}
                <div>
                  <span className="font-medium">Hedef:</span> {rule?.destination}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Add Queue Modal */}
      {showAddQueueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Yeni Kuyruk Ekle</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddQueueModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Kuyruk Adı"
                  type="text"
                  value={newQueue?.name}
                  onChange={(e) => setNewQueue({...newQueue, name: e?.target?.value})}
                  placeholder="Satış Kuyruğu"
                  required
                />
                <Input
                  label="Dahili Numara"
                  type="text"
                  value={newQueue?.extension}
                  onChange={(e) => setNewQueue({...newQueue, extension: e?.target?.value})}
                  placeholder="8004"
                  required
                />
                <Select
                  label="Dağıtım Stratejisi"
                  options={strategyOptions}
                  value={newQueue?.strategy}
                  onChange={(value) => setNewQueue({...newQueue, strategy: value})}
                />
                <Input
                  label="Zaman Aşımı (saniye)"
                  type="number"
                  value={newQueue?.timeout}
                  onChange={(e) => setNewQueue({...newQueue, timeout: parseInt(e?.target?.value)})}
                  placeholder="30"
                />
                <Input
                  label="Maksimum Bekleme (saniye)"
                  type="number"
                  value={newQueue?.maxWaitTime}
                  onChange={(e) => setNewQueue({...newQueue, maxWaitTime: parseInt(e?.target?.value)})}
                  placeholder="300"
                />
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-4">Kuyruk Ayarları</h4>
                <div className="space-y-3">
                  <Checkbox
                    label="Bekletme Müziği"
                    description="Bekleyen arayanlar için müzik çal"
                    checked={newQueue?.musicOnHold}
                    onChange={(e) => setNewQueue({...newQueue, musicOnHold: e?.target?.checked})}
                  />
                  <Checkbox
                    label="Sıra Duyurusu"
                    description="Arayana kuyrukta kaçıncı sırada olduğunu söyle"
                    checked={newQueue?.announcePosition}
                    onChange={(e) => setNewQueue({...newQueue, announcePosition: e?.target?.checked})}
                  />
                  <Checkbox
                    label="Arama Kaydı"
                    description="Bu kuyruktaki aramaları kaydet"
                    checked={newQueue?.recordCalls}
                    onChange={(e) => setNewQueue({...newQueue, recordCalls: e?.target?.checked})}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowAddQueueModal(false)}
              >
                İptal
              </Button>
              <Button
                variant="default"
                onClick={handleAddQueue}
                iconName="Plus"
                iconPosition="left"
              >
                Kuyruk Ekle
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Add Rule Modal */}
      {showAddRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Yeni Yönlendirme Kuralı</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddRuleModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <Input
                label="Kural Adı"
                type="text"
                value={newRule?.name}
                onChange={(e) => setNewRule({...newRule, name: e?.target?.value})}
                placeholder="Yeni Yönlendirme Kuralı"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Koşul Tipi"
                  options={conditionOptions}
                  value={newRule?.condition}
                  onChange={(value) => setNewRule({...newRule, condition: value})}
                />
                <Input
                  label="Öncelik"
                  type="number"
                  value={newRule?.priority}
                  onChange={(e) => setNewRule({...newRule, priority: parseInt(e?.target?.value)})}
                  placeholder="1"
                  description="Düşük sayı yüksek öncelik"
                />
              </div>

              {newRule?.condition === 'time-based' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Başlangıç Saati"
                    type="time"
                    value={newRule?.timeStart}
                    onChange={(e) => setNewRule({...newRule, timeStart: e?.target?.value})}
                  />
                  <Input
                    label="Bitiş Saati"
                    type="time"
                    value={newRule?.timeEnd}
                    onChange={(e) => setNewRule({...newRule, timeEnd: e?.target?.value})}
                  />
                </div>
              )}

              {newRule?.condition === 'caller-id' && (
                <Input
                  label="Arayan Numara Deseni"
                  type="text"
                  value={newRule?.callerPattern}
                  onChange={(e) => setNewRule({...newRule, callerPattern: e?.target?.value})}
                  placeholder="+90532*"
                  description="* joker karakter olarak kullanılabilir"
                />
              )}

              <Select
                label="Hedef"
                options={destinationOptions}
                value={newRule?.destination}
                onChange={(value) => setNewRule({...newRule, destination: value})}
              />

              <Checkbox
                label="Kuralı Etkinleştir"
                checked={newRule?.enabled}
                onChange={(e) => setNewRule({...newRule, enabled: e?.target?.checked})}
              />
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowAddRuleModal(false)}
              >
                İptal
              </Button>
              <Button
                variant="default"
                onClick={handleAddRule}
                iconName="Plus"
                iconPosition="left"
              >
                Kural Ekle
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallRoutingTab;